import type { ShiftReturnType } from "@/hooks/roster-craete/useShiftsQuery";
import {
  buildShiftIdToGroupMainLetterMap,
  buildShiftLookupMaps,
  getDefaultShiftsSortedBySequence,
  getManpowerBucketForResolvedShift,
  getShiftMainTemplateCode,
  mergeShiftsForCellLookup,
  resolveShiftFromScheduleCellFromMaps,
  shouldPreferShiftDefinition,
  type ShiftLookupMaps,
} from "@/utils/shift-util";

export type ShiftCatalogShift = {
  readonly shift_id: string;
  readonly name?: string;
  readonly default_shift?: string | null;
  readonly shift_gb?: string | null;
  readonly sequence?: number;
  readonly id?: number;
  readonly color?: string;
};

export type ShiftCatalog = {
  readonly shifts: ShiftCatalogShift[];
  readonly lookupMaps: ShiftLookupMaps<ShiftCatalogShift>;
  readonly defaultShiftToCanonical: ReadonlyMap<
    string,
    { readonly id: number; readonly shift_id: string }
  >;
  readonly shiftIdToGroupMainLetter: ReadonlyMap<string, string>;
  readonly resolveCell: (
    code: string | null | undefined,
    scheduleId: number | null | undefined
  ) => ShiftCatalogShift | undefined;
  readonly getColorForCell: (
    code: string | null | undefined,
    scheduleId: number | null | undefined,
    fallback?: string
  ) => string;
  readonly getManpowerBucket: (
    resolved: ShiftCatalogShift | undefined,
    validateKeys: Set<string>
  ) => string | null;
};

const DEFAULT_CELL_COLOR = "#F0F0F2";

function dedupeMergedShiftRows(
  shifts: readonly ShiftCatalogShift[]
): ShiftCatalogShift[] {
  const byKey = new Map<string, ShiftCatalogShift>();

  shifts.forEach((shift) => {
    const templateCode = getShiftMainTemplateCode(shift) ?? "";
    const key = `${templateCode}:${shift.shift_id}`;
    const current = byKey.get(key);
    if (shouldPreferShiftDefinition(current, shift)) {
      byKey.set(key, shift);
    }
  });

  return [...byKey.values()];
}

export function createShiftCatalog(
  defaultShiftCode: ShiftCatalogShift[],
  shiftsData: ShiftReturnType | ShiftCatalogShift[] | undefined | null
): ShiftCatalog {
  const merged: ShiftCatalogShift[] = dedupeMergedShiftRows(
    mergeShiftsForCellLookup(defaultShiftCode, shiftsData ?? undefined)
  );
  const lookupMaps = buildShiftLookupMaps(merged);
  const shiftIdToGroupMainLetter = buildShiftIdToGroupMainLetterMap(merged, {
    includeMid: true,
  });
  const defaultShiftToCanonical = new Map<
    string,
    { readonly id: number; readonly shift_id: string }
  >();

  getDefaultShiftsSortedBySequence(merged, { excludeOff: true }).forEach(
    (shift) => {
      const defaultShift = shift.default_shift;
      if (!defaultShift || defaultShiftToCanonical.has(defaultShift)) return;
      if (shift.id == null || !Number.isFinite(Number(shift.id))) return;
      defaultShiftToCanonical.set(defaultShift, {
        id: Number(shift.id),
        shift_id: shift.shift_id,
      });
    }
  );

  const resolveCell: ShiftCatalog["resolveCell"] = (code, scheduleId) =>
    resolveShiftFromScheduleCellFromMaps(code, scheduleId, lookupMaps);

  const getColorForCell: ShiftCatalog["getColorForCell"] = (
    code,
    scheduleId,
    fallback = DEFAULT_CELL_COLOR
  ) => resolveCell(code, scheduleId)?.color ?? fallback;

  const getManpowerBucket: ShiftCatalog["getManpowerBucket"] = (
    resolved,
    validateKeys
  ) =>
    getManpowerBucketForResolvedShift(
      resolved,
      new Map(shiftIdToGroupMainLetter),
      validateKeys
    );

  return {
    shifts: merged,
    lookupMaps,
    defaultShiftToCanonical,
    shiftIdToGroupMainLetter,
    resolveCell,
    getColorForCell,
    getManpowerBucket,
  };
}
